import React, { useEffect, useRef, useState } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import { refreshBranding } from '../hooks/useBranding';

interface BrandingConfig {
  logoUrl: string;
  logoConfigured: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

// Must match the backend allow-list and size cap in admin/index.js.
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
const MAX_BYTES = 50 * 1024; // 50 KB

// Reads a File into a base64 string (no data: prefix), for the JSON upload body.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is a data: URI — strip the prefix, keep only the base64 payload.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const BrandingSettings: React.FC = () => {
  const [config, setConfig] = useState<BrandingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Local object-URL preview of a freshly selected (not-yet-saved) file.
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/branding');
      setConfig(response);
    } catch (error) {
      console.error('Error loading branding settings:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // Clean up any object URL on unmount.
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Unsupported image type. Use PNG, JPEG, SVG, or WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Logo must be 50 KB or smaller.');
      return;
    }

    if (preview) URL.revokeObjectURL(preview);
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const data = await fileToBase64(selectedFile);
      const response = await apiCall('/admin/branding/logo', {
        method: 'POST',
        body: JSON.stringify({ contentType: selectedFile.type, data })
      });

      toast.success('Logo uploaded successfully');
      // Clear the branding cache so Login/Layout re-fetch the new logo.
      refreshBranding();

      // Reset local selection and reload stored config.
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setConfig((prev) => ({
        logoUrl: response?.logoUrl || prev?.logoUrl || '/branding/logo.png',
        logoConfigured: true,
        updatedAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-24 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  // Cache-bust the stored logo so the admin sees their latest upload immediately
  // (the browser may otherwise show a stale image).
  const storedLogoSrc = config?.logoConfigured && config?.logoUrl
    ? `${config.logoUrl}?t=${encodeURIComponent(config.updatedAt || '')}`
    : null;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Brand Logo</h3>
        <p className="text-sm text-gray-600">
          Upload your organization's logo. It appears on the login screen, the dashboard header,
          and the welcome email sent to new users. PNG, JPEG, SVG, or WebP up to 50&nbsp;KB.
          For best results in emails, use a wide logo around 180&times;48 pixels.
        </p>
      </div>

      <div className="space-y-6">
        {/* Current logo */}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-2">Current logo</span>
          <div className="flex items-center justify-center h-24 w-64 bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
            {storedLogoSrc ? (
              <img src={storedLogoSrc} alt="Current logo" className="max-h-20 max-w-full object-contain" />
            ) : (
              <span className="text-sm text-gray-400">No custom logo (default is shown)</span>
            )}
          </div>
        </div>

        {/* New logo preview */}
        {preview && (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">New logo preview</span>
            <div className="flex items-center justify-center h-24 w-64 bg-gray-50 border border-blue-200 rounded-md overflow-hidden">
              <img src={preview} alt="New logo preview" className="max-h-20 max-w-full object-contain" />
            </div>
          </div>
        )}

        <div>
          <input
            ref={fileInputRef}
            id="branding-logo-file"
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            onChange={handleFileSelect}
            className="block text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
        {selectedFile && (
          <button
            onClick={handleCancel}
            disabled={uploading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? 'Uploading...' : 'Upload Logo'}
        </button>
      </div>
    </div>
  );
};

export default BrandingSettings;
